import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { TieredPricing } from "@/data/cars";

interface CarInfo {
  name: string;
  year: number;
  pricing: TieredPricing;
  image: string;
  slug: string;
  offRoadAllowed: boolean;
}

interface BookingData {
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: Date | undefined;
  dropoffDate: Date | undefined;
  selectedCar: CarInfo | null;
}

interface BookingContextType {
  bookingData: BookingData;
  setPickupLocation: (location: string) => void;
  setDropoffLocation: (location: string) => void;
  setPickupDate: (date: Date | undefined) => void;
  setDropoffDate: (date: Date | undefined) => void;
  setSelectedCar: (car: CarInfo | null) => void;
  getRentalDays: () => number;
}

const defaultBookingData: BookingData = {
  pickupLocation: "",
  dropoffLocation: "",
  pickupDate: undefined,
  dropoffDate: undefined,
  selectedCar: null,
};

const BookingContext = createContext<BookingContextType | undefined>(undefined);

const STORAGE_KEY = "fsta_booking_data";

export const BookingProvider = ({ children }: { children: ReactNode }) => {
  const [bookingData, setBookingData] = useState<BookingData>(() => {
    // Try to restore from sessionStorage on initial load
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          pickupDate: parsed.pickupDate ? new Date(parsed.pickupDate) : undefined,
          dropoffDate: parsed.dropoffDate ? new Date(parsed.dropoffDate) : undefined,
          selectedCar: parsed.selectedCar || null,
        };
      }
    } catch {
      // Ignore errors
    }
    return defaultBookingData;
  });

  // Persist to sessionStorage whenever data changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bookingData));
    } catch {
      // Ignore errors
    }
  }, [bookingData]);

  const setPickupLocation = (location: string) => {
    try {
      setBookingData((prev) => ({ 
        ...prev, 
        pickupLocation: location,
        dropoffLocation: !prev.dropoffLocation || prev.dropoffLocation === prev.pickupLocation 
          ? location 
          : prev.dropoffLocation
      }));
    } catch (e) {
      console.error("Error setting pickup location:", e);
    }
  };

  const setDropoffLocation = (location: string) => {
    try {
      setBookingData((prev) => ({ ...prev, dropoffLocation: location }));
    } catch (e) {
      console.error("Error setting dropoff location:", e);
    }
  };

  const setPickupDate = (date: Date | undefined) => {
    try {
      setBookingData((prev) => ({ ...prev, pickupDate: date }));
    } catch (e) {
      console.error("Error setting pickup date:", e);
    }
  };

  const setDropoffDate = (date: Date | undefined) => {
    try {
      setBookingData((prev) => ({ ...prev, dropoffDate: date }));
    } catch (e) {
      console.error("Error setting dropoff date:", e);
    }
  };

  const setSelectedCar = (car: CarInfo | null) => {
    setBookingData((prev) => ({ ...prev, selectedCar: car }));
  };

  const getRentalDays = (): number => {
    if (!bookingData.pickupDate || !bookingData.dropoffDate) return 1;
    const diffTime = bookingData.dropoffDate.getTime() - bookingData.pickupDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    // Add 1 to include both start and end day (1st to 5th = 5 days, not 4)
    return Math.max(1, diffDays + 1);
  };

  return (
    <BookingContext.Provider
      value={{
        bookingData,
        setPickupLocation,
        setDropoffLocation,
        setPickupDate,
        setDropoffDate,
        setSelectedCar,
        getRentalDays,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = (): BookingContextType => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
};

export const locations = [
  { id: "tbilisi", label: "Tbilisi", country: "Georgia" },
  { id: "kutaisi", label: "Kutaisi", country: "Georgia" },
  { id: "batumi", label: "Batumi", country: "Georgia" },
  { id: "yerevan", label: "Yerevan", country: "Armenia" },
  { id: "gyumri", label: "Gyumri", country: "Armenia" },
  { id: "dilijan", label: "Dilijan", country: "Armenia" },
  { id: "istanbul", label: "Istanbul", country: "Turkey" },
  { id: "antalya", label: "Antalya", country: "Turkey" },
  { id: "izmir", label: "Izmir", country: "Turkey" },
  { id: "ankara", label: "Ankara", country: "Turkey" },
  { id: "trabzon", label: "Trabzon", country: "Turkey" },
  { id: "nevsehir-kayseri", label: "Nevşehir / Kayseri", country: "Turkey" },
];
